import { FunctionComponent, useMemo, useState } from 'react';
import { Box, GlobalStyles, Paper, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import { MakeOptional } from '@mui/x-date-pickers/internals';
import { ChartsTooltip, PiePlot, PieValueType, ResponsiveChartContainer } from '@mui/x-charts';
import { pieArcLabelClasses } from '@mui/x-charts/PieChart';
import gaiaTheme from '../../../../root-components/theme';
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils';
import { applyTopNWithOther, TopNValue } from '../../../../shared/utils/analytics-utils';
import { TopNSelect } from './top-n-select';

const LEGEND_LIMIT = 10;

export const GraphDonutChart: FunctionComponent<{
  backgroundColor?: string,
  textColor?: string,
  title: string,
  tooltip?: string,
  colors?: string[],
  dataset: Array<{ unit?: string, value: number, label: string }>,
  energyValue?: boolean,
  chartHeight?: number,
  paperPadding?: number,
  titleVariant?: 'body2' | 'subtitle1',
  paperHeight?: number | string,
  /** Show a Top N dropdown that re-slices the dataset (with "Other") client-side. */
  enableTopN?: boolean,
  defaultTopN?: TopNValue,
  /** Hide the title/tooltip header row (e.g. when the caller renders its own fixed title). */
  hideHeader?: boolean,
  /**
   * A second period surfaced in each slice's hover tooltip ("2026: X · 2025: Y").
   * Deliberately not drawn as a second ring: the visual stays one period.
   */
  comparison?: { dataset: Array<{ value: number, label: string }>, label: string },
  /** Names the selected period in tooltips when a comparison is present. */
  periodLabel?: string,
  /**
   * Click-to-drill: fires with the clicked slice's data row (from the DISPLAYED, post-Top-N
   * dataset) and its index. Aggregate "Other" / "Other (N more)" slices are never reported.
   */
  onSelect?: (item: any, dataIndex: number) => void,
}> = ({
  title, tooltip, dataset,
  backgroundColor = 'white',
  textColor = 'text.primary',
  colors,
  energyValue = false,
  chartHeight = 325,
  paperPadding = 4,
  titleVariant = 'subtitle1',
  paperHeight = '100%',
  enableTopN = false,
  defaultTopN = 5,
  hideHeader = false,
  comparison,
  periodLabel,
  onSelect,
}) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
    const theme = useTheme();
    const resolvedColors = colors || getMixedColorPalette(theme.palette.primary.main);

    const [topN, setTopN] = useState<TopNValue>(defaultTopN);
    // A donut can't meaningfully render more than a few dozen slices (each becomes
    // sub-pixel and the tail fades out), so hard-cap it: the largest slices are kept
    // and the rest collapse into a single "Other (N more)" wedge. The bar chart is
    // NOT capped, so it remains the "see every category" view.
    const DONUT_MAX_SLICES = 75;
    const displayed = useMemo(() => {
      const base = (enableTopN
        ? applyTopNWithOther(dataset, topN, 'value')
        : dataset) as Array<{ value: number; label: string }>;
      if (base.length <= DONUT_MAX_SLICES) return base;
      const sorted = [...base].sort((a, b) => b.value - a.value);
      const top = sorted.slice(0, DONUT_MAX_SLICES);
      const rest = sorted.slice(DONUT_MAX_SLICES);
      const otherValue = rest.reduce((sum, item) => sum + (item.value || 0), 0);
      return [...top, { label: `Other (${rest.length} more)`, value: otherValue }];
    }, [enableTopN, dataset, topN]);

    const totalValue = displayed.reduce((sum, item) => sum + item.value, 0);
    // With many slices, give every non-zero segment a ~thin visible arc by flooring
    // the RENDERED value (labels/tooltips still use the real `displayed` value, so
    // numbers stay truthful). The floor is BUDGET-BOUNDED: a very long tail (e.g.
    // "All" with hundreds of tiny categories) would otherwise inflate the total and
    // squash/smear the real slices, so the flooring may add at most ~15% of the
    // circle overall. Beyond that the tail renders at true (tiny) size.
    const manySlices = displayed.length > 8;
    const rawMin = totalValue * 0.004;
    const tinyCount = displayed.filter((item) => item.value > 0 && item.value < rawMin).length;
    const minValue = tinyCount > 0 ? Math.min(rawMin, (totalValue * 0.15) / tinyCount) : rawMin;
    const renderData = manySlices
      ? displayed.map((item) => ({ ...item, value: item.value > 0 ? Math.max(item.value, minValue) : item.value }))
      : displayed;
    const colorAt = (index: number) => resolvedColors[index % resolvedColors.length];
    const legendVisible = displayed.slice(0, LEGEND_LIMIT);
    const legendHidden = displayed.slice(LEGEND_LIMIT);

    // Comparison values aligned to the displayed slices (the "Other" slice absorbs the
    // comparison values of everything not individually shown, so both periods account for
    // their full totals). Surfaced in the hover tooltip only; the drawn chart stays one period.
    const hasComparison = !!comparison && Array.isArray(comparison.dataset);
    const comparisonAligned = useMemo(() => {
      if (!hasComparison) return [];
      const byLabel = new Map(comparison!.dataset.map((item) => [String(item.label), Number(item.value || 0)]));
      const comparisonTotal = comparison!.dataset.reduce((sum, item) => sum + Number(item.value || 0), 0);
      const namedLabels = displayed.filter((item) => !String(item.label).startsWith('Other')).map((item) => String(item.label));
      const namedSum = namedLabels.reduce((sum, label) => sum + (byLabel.get(label) ?? 0), 0);
      return displayed.map((item) => (
        String(item.label).startsWith('Other')
          ? Math.max(0, comparisonTotal - namedSum)
          : (byLabel.get(String(item.label)) ?? 0)
      ));
    }, [hasComparison, comparison, displayed]);
    const comparisonTotalValue = comparisonAligned.reduce((sum, value) => sum + value, 0);
    const formatSliceValue = (raw: number, total: number): string => {
      const formatted = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(raw);
      const percentage = total > 0 ? Math.floor((raw / total) * 100) : 0;
      return `${formatted}${energyValue ? ' kWh' : ' tCO2e'} (${percentage}%)`;
    };

    return (
      <Paper variant='outlined' sx={{ p: paperPadding, height: paperHeight, backgroundColor: backgroundColor, border: backgroundColor === 'primary.dark' ? 'none' : '' }}>
        {/* The tooltip mounts in a portal outside this tree (and ChartsTooltip's slotProps
            silently disables it in this x-charts version), so the one-line-per-period
            rendering comes from a global rule on the tooltip's value cell. */}
        {hasComparison && (
          <GlobalStyles styles={{ '.MuiChartsTooltip-valueCell, .MuiChartsTooltip-root td:last-of-type': { whiteSpace: 'pre-line' } }} />
        )}
        {!hideHeader && <Stack direction='row' alignItems={'center'} spacing={1}>
          <Typography variant={titleVariant} sx={{ flex: 1, color: textColor }}>
            <b>{title}</b>
          </Typography>
          {tooltip && !isMobileMode && <TooltipButton
            text={tooltip}
            noPadding={true}
            placement='right' />}
          {enableTopN && <TopNSelect value={topN} onChange={setTopN} />}
        </Stack>}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <ResponsiveChartContainer
            height={chartHeight}
            series={[{
              type: 'pie',
              data: renderData,
              cx: '50%',
              cy: '50%',
              // Real % from the true `displayed` value (renderData floors the arc, not the number).
              arcLabel: (item) => {
                const real = displayed[item.index]?.value ?? item.value;
                return `${totalValue > 0 ? Math.floor((real / totalValue) * 100) : 0}%`;
              },
              arcLabelMinAngle: 20,
              arcLabelRadius: 65,
              innerRadius: 40,
              outerRadius: 100,
              paddingAngle: manySlices ? 0 : 2,
              cornerRadius: manySlices ? 0 : 5,
              highlightScope: { faded: 'global', highlighted: 'item' },
              faded: { innerRadius: 40, additionalRadius: -15, color: 'gray' },
              // This is the text the hover tooltip actually renders (after the slice's label),
              // so the comparison period rides along here when one is set.
              valueFormatter: (value: MakeOptional<PieValueType, 'id'>): string => {
                const raw = value.value || 0;
                const current = formatSliceValue(raw, totalValue);
                if (!hasComparison) return current;
                const index = displayed.findIndex((item) => String(item.label) === String(value.label));
                const comparisonValue = formatSliceValue(index >= 0 ? (comparisonAligned[index] ?? 0) : 0, comparisonTotalValue);
                // Rendered on two lines (one per period) via the tooltip's pre-line value cell.
                return `${periodLabel || 'This period'}: ${current}\n${comparison!.label}: ${comparisonValue}`;
              },
            }]}
            colors={resolvedColors}
            sx={{
              ...(onSelect ? { '& .MuiPieArc-root': { cursor: 'pointer' } } : {}),
              [`& .${pieArcLabelClasses.root}`]: {
                fill: 'white',
                fontWeight: 600,
              },
            }}
          >
            <PiePlot
              onItemClick={onSelect
                ? ((_event: any, slice: { dataIndex: number }) => {
                  const clicked: any = displayed[slice.dataIndex];
                  if (clicked && !String(clicked.label).startsWith('Other')) onSelect(clicked, slice.dataIndex);
                })
                : undefined}
            />
            <ChartsTooltip
              trigger="item"
              formatter={(value: number, context: { series: any, dataIndex?: number, index?: number }) => {
                const dataIndex = (context.dataIndex ?? context.index ?? 0) as number;
                const item = displayed[dataIndex];
                if (!item) return `${value}`;
                const percentage = totalValue > 0 ? Math.floor((item.value / totalValue) * 100) : 0;
                const formatted = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.value);
                return `${item.label}: ${formatted} tCO2e (${percentage}%)`;
              }}
            />
          </ResponsiveChartContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', alignSelf: 'stretch' }}>
            {legendVisible.map((entry, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <Box sx={{
                  width: 20, height: 20, borderRadius: '5px',
                  backgroundColor: colorAt(index)
                }}></Box>
                <Typography sx={{ mr: 2, color: textColor }}>{entry.label}</Typography>
              </Box>
            ))}
            {legendHidden.length > 0 && (
              <Tooltip
                arrow
                placement='top'
                slotProps={{ tooltip: { sx: { maxWidth: 560 } } }}
                title={
                  <Box
                    sx={{
                      maxHeight: '50vh',
                      overflowY: 'auto',
                      display: 'grid',
                      gridTemplateColumns: legendHidden.length > 12 ? '1fr 1fr' : '1fr',
                      columnGap: 1.5,
                      rowGap: 0.25,
                      p: 0.5,
                    }}
                  >
                    {legendHidden.map((entry, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', flexShrink: 0, backgroundColor: colorAt(LEGEND_LIMIT + i) }} />
                        <Typography variant='caption' noWrap>{entry.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                }
              >
                <Typography sx={{ mr: 2, color: 'primary.main', cursor: 'default', textDecoration: 'underline dotted' }}>
                  +{legendHidden.length} more
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>
    )
  }
